/* eslint-disable no-throw-literal */
import React, { useState, useEffect } from "react";
import "../App.scss";
import { useDispatch } from "react-redux";
import { headerDisplaying, footerDisplaying } from "../store/mainSlice";
import { isLoadChanger } from "../store/mainSlice";
import { useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import swalDialog from "../helpers/swalErrorPopup";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import userAPI from "../api/userAPI";
import HealthDeclarationDialog from "../components/HealthDeclarationDialog";
import HealthDeclarationDialogWOW from "../components/HealthDeclarationDialogWOW";
import { isPartnerClalit } from "../store/partnerStore";

export default function RegistrationQuestionnaire() {
  const { t } = useTranslation();
  const isClalit = useSelector(isPartnerClalit);
  const questions = t("REGISTRATION_QUESTIONS.QUESTIONS", { returnObjects: true });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Set only by the onboarding chain (OTP / registration). The health form is also
  // shown to existing users on renewal — LayoutV2 navigates here with no state, and
  // those users must exit to home, not to the one-time welcome intro.
  const fromOnboarding = useLocation().state?.fromOnboarding === true;
  const [answers, setAnswers] = useState({});
  const [agreement, setAgreement] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isNotCkickedAgreement, setIsNotCkickedAgreement] = useState(false);
  const mainUserData = useSelector((state) => state.userSlice.mainUserData);
  const [refs, setRefs] = useState([]);
  const [, setMissedElem] = useState(null);

  const handleClose = () => {
    setIsOpen(false);
  };

  const scrollToElement = (id) => {
    const element = refs.find((elem) => elem.Id === id);
    if (element) {
      element.ref.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  };

  function findFirstMissingElement(arr, max) {
    for (let i = 1; i <= max; i++) {
      if (!arr.includes(i)) {
        return i;
      }
    }
    return null;
  }

  const sendAnswers = async (answers, agreement) => {
    try {
      if (Object.keys(answers).length < 13) {
        const questionsIds = Object.keys(answers).map(Number);
        const missedElem = findFirstMissingElement(questionsIds, 13);
        setMissedElem(missedElem);
        scrollToElement(missedElem);

        setOpenTooltip(true);
      } else {
        dispatch(isLoadChanger(true));
        const resp = await userAPI.confirmClientHealthConditionStatement({
          id: mainUserData.ID,
          token: mainUserData.Token,
          phone: mainUserData.Phone,
          confirmed: agreement,
          answers: answers,
        });
        if (resp.data.Error !== 0 && resp.data.Error !== 108) {
          throw {
            code: resp.data.Error,
            mess: resp.data.Message,
          };
        }
        if (resp.data.Error !== 108) {
          // First onboarding ends at the welcome intro; a renewal goes home.
          navigate(fromOnboarding ? "/welcome" : "/");

          dispatch(isLoadChanger(false));
        } else {
          setIsOpen(true);
          dispatch(isLoadChanger(false));
        }
      }
    } catch (err) {
      dispatch(isLoadChanger(false));
      swalDialog(err);
    }
  };

  useEffect(() => {
    dispatch(headerDisplaying(false));
    dispatch(footerDisplaying(false));
    return () => {
      dispatch(headerDisplaying(true));
    };
  }, []);

  return (
    <div className="registration-questionnaire-wrapper">
      <div className="registration-questionnaire-subheader">
        <h2>{t("REGISTRATION_QUESTIONS.PART_A_TITLE")}</h2>
        <p>{t("REGISTRATION_QUESTIONS.PART_A_INSTRUCTION")}</p>
      </div>
      <div className="questionnaire-container">
        {questions.map((el, i) => (
          <div
            key={`question-${i + 1}`}
            className="questionnaire-elem"
            ref={(el) =>
              setRefs((prev) => {
                prev.push({ Id: i + 1, ref: el });
                return prev;
              })
            }
          >
            <div className="questionnaire-text-in-white-block-container">
              <p>{el}</p>
              <span className="questionnaire-text-ml5 test">{`.${i + 1}`}</span>
            </div>
            <div className="questionnaire-elem-checkbox-container">
              <div className="questionnaire-checkbox-wrapper">
                <p className="questionnaire-checkbox-label">{t("REGISTRATION_QUESTIONS.NO")}</p>
                <label className="questionnaire-custom-checkbox">
                  <input
                    type="checkbox"
                    checked={answers[i + 1] === false}
                    onChange={() => {
                      setAnswers((prev) => ({ ...prev, [i + 1]: false }));
                    }}
                  />
                  <span className="checkmark"></span>
                </label>
              </div>
              <div className="questionnaire-checkbox-wrapper">
                <p className="questionnaire-checkbox-label">{t("REGISTRATION_QUESTIONS.YES")}</p>
                <label className="questionnaire-custom-checkbox">
                  <input
                    type="checkbox"
                    checked={answers[i + 1] === true}
                    onChange={() => {
                      setAnswers((prev) => ({ ...prev, [i + 1]: true }));
                    }}
                  />
                  <span className="checkmark"></span>
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="questionnaire-instruction-block">
        <div className="questionnaire-text-header">
          <h2 className="questionnaire-header-text">{t("REGISTRATION_QUESTIONS.PART_B_TITLE")}</h2>
        </div>
        <div className="questionnaire-elem">
          <p className="questionnaire-text-in-white-block-container">
            <span className="questionnaire-text-ml5"> 1. </span>
            <span className="questionnaire-text-in-white-block">{t("REGISTRATION_QUESTIONS.INSTRUCTION_1")}</span>
          </p>
        </div>
        <div className="questionnaire-elem">
          <p className="questionnaire-text-in-white-block-container">
            <span className="questionnaire-text-ml5">2.</span>
            <span className="questionnaire-text-in-white-block">{t("REGISTRATION_QUESTIONS.INSTRUCTION_2")}</span>
          </p>
        </div>
        <div className="questionnaire-elem">
          <p className="questionnaire-text-in-white-block-container">
            <span className="questionnaire-text-ml5">3.</span>
            <span className="questionnaire-text-in-white-block">{t("REGISTRATION_QUESTIONS.INSTRUCTION_3")}</span>
          </p>
        </div>
        <div className="questionnaire-text-header">
          <h2 className="questionnaire-header-text">{t("REGISTRATION_QUESTIONS.PART_C_TITLE")}</h2>
        </div>
        <div className="questionnaire-elem">
          <p className="questionnaire-text-in-white-block">{t("REGISTRATION_QUESTIONS.DECLARATION")}</p>
        </div>
      </div>
      <div className="questionnaire-btn-block">
        <div className="questionnaire-checkbox-wrapper">
          <p className="questionnaire-checkbox-label">{t("REGISTRATION_QUESTIONS.I_CONFIRM")}</p>
          <label className="questionnaire-custom-checkbox">
            <input
              type="checkbox"
              checked={agreement}
              onChange={(e) => {
                setIsNotCkickedAgreement(false);
                setAgreement(e.target.checked);
              }}
            />
            <span className="checkmark" style={isNotCkickedAgreement ? { borderColor: "red" } : {}}></span>
          </label>
        </div>
        <Tooltip
          open={openTooltip}
          onClose={() => setOpenTooltip(false)}
          title={t("REGISTRATION_QUESTIONS.ANSWER_ALL_QUESTIONS")}
          leaveDelay={3000}
        >
          <button
            className="registration-btn"
            onClick={
              agreement
                ? () => sendAnswers(answers, agreement)
                : () => {
                    setIsNotCkickedAgreement(true);
                  }
            }
            disabled={!agreement}
          >
            {t("REGISTRATION_QUESTIONS.CONTINUE")}
          </button>
        </Tooltip>

        {isClalit ? (
          <HealthDeclarationDialog open={isOpen} onClose={handleClose} />
        ) : (
          <HealthDeclarationDialogWOW open={isOpen} onClose={handleClose} />
        )}
      </div>
    </div>
  );
}
